import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, X } from "lucide-react";
import { API } from "../../App";

// O sino do próprio piloto, aluno ou instrutor (pedido de 22/10): documentos que pedem atenção, registos de voo recusados
// e reservas canceladas por outra pessoa. O número conta só os avisos ainda não vistos neste aparelho.
const CHAVE_VISTOS = "sino-pessoal-vistos";
const COR = {
  critico: "border-red-500/40 bg-red-500/10 text-red-200",
  aviso: "border-amber-500/40 bg-amber-500/10 text-amber-200",
  info: "border-sky-500/30 bg-sky-500/10 text-sky-200",
};

const lerVistos = () => {
  try { return new Set(JSON.parse(window.localStorage.getItem(CHAVE_VISTOS) || "[]")); } catch { return new Set(); }
};
const guardarVistos = (chaves) => {
  try { window.localStorage.setItem(CHAVE_VISTOS, JSON.stringify(chaves)); } catch { /* sem armazenamento */ }
};

export default function SinoPessoal() {
  const navigate = useNavigate();
  const [avisos, setAvisos] = useState(null);
  const [aberto, setAberto] = useState(false);
  const [vistos, setVistos] = useState(lerVistos);
  const caixa = useRef(null);

  const carregar = useCallback(() => {
    axios.get(`${API}/di/sino/me`, { withCredentials: true })
      .then((r) => setAvisos(r.data?.avisos || []))
      .catch(() => setAvisos(null));   // sem ficha ligada: não há sino
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Fecha ao tocar fora
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e) => { if (caixa.current && !caixa.current.contains(e.target)) setAberto(false); };
    document.addEventListener("pointerdown", fora);
    return () => document.removeEventListener("pointerdown", fora);
  }, [aberto]);

  if (avisos === null) return null;
  const novos = avisos.filter((a) => !vistos.has(a.chave)).length;

  const abrir = () => {
    const vaiAbrir = !aberto;
    setAberto(vaiAbrir);
    if (vaiAbrir) {
      // Ao abrir, tudo o que está na lista fica visto (só se guardam as chaves atuais)
      const chaves = avisos.map((a) => a.chave);
      guardarVistos(chaves);
      setVistos(new Set(chaves));
    }
  };

  return (
    <div className="relative" ref={caixa}>
      <button
        type="button"
        onClick={abrir}
        className="relative inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:text-amber-300"
        title="Avisos"
        aria-label={novos ? `Avisos: ${novos} novos` : "Avisos"}
        data-testid="sino-pessoal"
      >
        <Bell className="w-4 h-4" />
        {novos > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-red-500 px-1 text-[10px] leading-4 text-white" data-testid="sino-pessoal-numero">{novos}</span>
        )}
      </button>
      {aberto && (
        <div className="fixed left-4 right-4 top-16 z-50 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 rounded-lg border border-slate-700 bg-slate-900 p-3 shadow-xl" data-testid="sino-pessoal-lista">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-100">Avisos</span>
            <button type="button" onClick={() => setAberto(false)} className="text-slate-400 hover:text-slate-200" aria-label="Fechar"><X className="w-4 h-4" /></button>
          </div>
          {avisos.length === 0 && <p className="text-xs text-slate-400">Sem avisos. Está tudo em dia.</p>}
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {avisos.map((a) => (
              <button
                key={a.chave}
                type="button"
                onClick={() => { setAberto(false); if (a.destino) navigate(a.destino); }}
                className={`block w-full rounded-md border p-2 text-left text-xs ${COR[a.nivel] || COR.info}`}
                data-testid="sino-pessoal-aviso"
              >
                {a.texto}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
